import React, { useState, useRef, useMemo } from 'react'
import PropTypes from 'prop-types'
import { XIcon, PaperAirplaneIcon } from '@heroicons/react/solid'
import TipTapPrivatePost from '@/shared/TipTapPrivatePost'
import { ACTIVE_MENTION, MentionModal, MentionSuggestions, inlineItems, mentionCandidates } from '@/pages/Posts/MentionPicker'

import useQuery from '@/hooks/useQuery'

// Bodies are HTML. An editor that has only ever been focused holds "<p></p>".
const isBlank = (html) => !html || html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim() === ''

const PrivatePostMessageForm = ({ postId, addPostMessage, replyTarget, onCancelReply, focusKey, people, me, ownerId }) => {
  const [customError, setCustomError] = useState(null)
  const [loading, setLoading] = useState(false)
  const { putpostRequest } = useQuery()
  const body = useRef(null)
  // A ref, not the state: Enter can land twice before a re-render, and the
  // old 300ms debounce that guarded this made every send feel laggy.
  const sending = useRef(false)
  const editor = useRef(null)

  // @mentions. `active` is the token being typed: { query, from, to } in
  // editor positions, or null when the caret isn't in one.
  const candidates = useMemo(() => mentionCandidates(people, me, ownerId), [people, me, ownerId])
  const [active, setActive] = useState(null)
  const [activeIndex, setActiveIndex] = useState(0)
  const [modalOpen, setModalOpen] = useState(false)
  const suggestions = useMemo(() => (active ? inlineItems(active.query, candidates) : null), [active, candidates])
  const pickerOpen = !!suggestions && suggestions.flat.length > 0

  const onCaret = (before, caret) => {
    const m = before.match(ACTIVE_MENTION)
    if (!m) { setActive(null); return }
    setActive(prev => (prev?.query === m[1] && prev?.to === caret ? prev : { query: m[1], from: caret - m[1].length - 1, to: caret }))
    setActiveIndex(0)
  }

  const insertMention = (item, range) => {
    const ed = editor.current
    if (!ed) return
    const chain = ed.chain().focus(undefined, { scrollIntoView: false })
    if (range) {
      chain.insertContentAt({ from: range.from, to: range.to }, `@${item.username} `).run()
    } else {
      // From the dialog: no token to replace, so it goes at the caret, with a
      // space in front when it would otherwise stick to the previous word,
      // since both parsers only see a mention at a word boundary.
      const { from } = ed.state.selection
      const prev = from > 1 ? ed.state.doc.textBetween(from - 1, from, '\n', '\n') : ''
      chain.insertContent(`${prev && !/\s/.test(prev) ? ' ' : ''}@${item.username} `).run()
    }
    setActive(null)
  }

  // First look at every key while the inline list is open.
  const onKeyDown = (event) => {
    if (!pickerOpen) return false
    const count = suggestions.flat.length
    if (event.key === 'ArrowDown') { setActiveIndex(i => (i + 1) % count); return true }
    if (event.key === 'ArrowUp') { setActiveIndex(i => (i - 1 + count) % count); return true }
    if (event.key === 'Enter' || event.key === 'Tab') { insertMention(suggestions.flat[activeIndex], active); return true }
    if (event.key === 'Escape') { setActive(null); return true }
    return false
  }

  const send = () => {
    if (sending.current || isBlank(body.current)) { return }
    sending.current = true
    setLoading(true)
    const sent = body.current

    const formData = {
      post_message: { body: sent, post_id: postId, reply_to_id: replyTarget?.id || null }
    }

    // Cleared now, like Discord, so anything typed while the request is in
    // flight belongs to the next message instead of being wiped when this one
    // lands. A failure puts the sent text back in front of whatever is there.
    editor.current?.commands.clearContent(true)

    putpostRequest('/api/v3/post_messages', 'POST', formData, (err, jsonData) => {
      sending.current = false
      setLoading(false)
      if (err) {
        setCustomError(typeof err === 'string' ? err : "I couldn't send that. Maybe the internet blinked?")
        const ed = editor.current
        if (ed) {
          const typedSince = isBlank(ed.getHTML()) ? '' : ed.getHTML()
          ed.commands.setContent(sent + typedSince, true)
        }
        return
      }

      setCustomError(null)
      addPostMessage(jsonData.postMessage)
    })
  }

  // Right padding clears the audio player's tab (Layout/FooterPlayer), which is
  // fixed to the bottom-right corner, right where the send button sits. When
  // the player bar is open it covers the bottom 4rem, so the composer rides
  // above it.
  return <div className='shrink-0 pl-3 sm:pl-4 pr-12 sm:pr-14 lg:pr-16 pt-2 pb-[max(0.75rem,env(safe-area-inset-bottom))] [.footer-player-open_&]:pb-20 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700'>
    { replyTarget && <div className='flex items-center justify-between gap-2 mb-1 px-2 py-1 rounded-t-md bg-gray-100 dark:bg-gray-700 text-xs text-gray-600 dark:text-gray-300'>
      <span className='truncate'>Replying to <b>{replyTarget.userDisplayName}</b></span>
      <button type='button' onClick={onCancelReply} aria-label='Cancel reply' className='text-gray-400 hover:text-gray-700 dark:hover:text-white'>
        <XIcon className='h-4 w-4' />
      </button>
    </div> }
    <div className='relative flex items-end gap-2'>
      {pickerOpen && <MentionSuggestions
        query={active.query}
        groups={suggestions.groups}
        people={suggestions.people}
        activeIndex={activeIndex}
        onPick={(item) => insertMention(item, active)}
        onHover={setActiveIndex}
      />}
      <button
        type='button'
        onClick={() => setModalOpen(true)}
        aria-label='Mention someone'
        title='Mention someone'
        className='shrink-0 h-10 w-10 flex items-center justify-center rounded-md border border-gray-200 dark:border-gray-600 text-gray-500 dark:text-gray-300 hover:text-cccblue hover:border-cccblue text-lg'
      >@</button>
      <div className='flex-1 min-w-0 max-h-48 overflow-y-auto'>
        <TipTapPrivatePost
          html={null}
          updateHtml={(val) => { body.current = val }}
          onEnter={send}
          focusKey={focusKey}
          onCaret={onCaret}
          onKeyDown={onKeyDown}
          onEditor={(ed) => { editor.current = ed }}
          placeholder='Say something nice.'
        />
      </div>
      <button
        type='button'
        onClick={send}
        disabled={loading}
        aria-label='Send'
        className='shrink-0 h-10 w-10 flex items-center justify-center rounded-md bg-cccblue hover:bg-cccblue-alt text-white disabled:opacity-50'
      >
        <PaperAirplaneIcon className='h-5 w-5 rotate-90' />
      </button>
    </div>
    { customError && <div className='mt-2 text-sm text-red-600'>{customError}</div> }
    <MentionModal
      open={modalOpen}
      onClose={() => setModalOpen(false)}
      candidates={candidates}
      onPick={(item) => { setModalOpen(false); insertMention(item, null) }}
    />
  </div>
}

export default PrivatePostMessageForm

PrivatePostMessageForm.propTypes = {
  postId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  addPostMessage: PropTypes.func.isRequired,
  replyTarget: PropTypes.object,
  onCancelReply: PropTypes.func,
  focusKey: PropTypes.any,
  people: PropTypes.array,
  me: PropTypes.number,
  ownerId: PropTypes.number
}
